import {
  ChapterRouteParams,
  createSection,
  deleteSection,
  SectionDTO,
  updateSection,
} from '@/data/services/sectionServices';
import { Section } from '@/models/Book';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { getErrorMessage } from '@/utils/getErrorMessage';
import compareBlocks from '@/utils/material/section/compareBlocks';
import alert from '@/utils/shared/alert';
import { useMutation } from '@tanstack/react-query';
import { cloneDeep, debounce, isEqual } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SectionForm from './form/SectionForm';

type SectionHocProps = {
  section: Section;
  onSubmit?: (section: Section) => void;
  onDelete?: (section: Section) => void;
  isUnique?: boolean;
} & ChapterRouteParams;

export default function SectionService({
  onSubmit,
  isUnique,
  onDelete: onDeleteSection,
  section: { id: sectionId, ...section },
  ...chapterParams
}: SectionHocProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'section',
  });

  const [hasChanges, setHasChanges] = useState(false);

  const handleCreate = async (section: SectionDTO) => {
    return createSection(chapterParams, section);
  };

  const handleUpdate = async (changes: Partial<Section>) => {
    if (!changes.content) return;

    const sectionChanges = buildChangedObject(section, changes, {
      ignoreFields: ['content'],
    });
    const hasContentChanges = !compareBlocks(
      section.content.blocks,
      changes.content.blocks,
    );

    if (hasContentChanges) sectionChanges.content = changes.content;

    if (Object.keys(sectionChanges).length === 0) return;

    const updatedSection = await updateSection(
      {
        ...chapterParams,
        sectionId,
      },
      sectionChanges,
    );

    return updatedSection;
  };

  const handleEdit = async ({
    section: newSection,
    validated,
  }: {
    section: Partial<Section>;
    validated: boolean;
  }) => {
    if (!validated) return;
    setHasChanges(false);
    return sectionId
      ? handleUpdate(newSection)
      : handleCreate({
          ...section,
          ...newSection,
        });
  };

  const { mutate: onEdit, isPending: isLoadingEdit } = useMutation(handleEdit, {
    onSuccess: data => {
      if (!data) return;
      onSubmit?.(data);
      setHasChanges(!isEqual(data, section));
    },
    onError: error => {
      const errorMessage = getErrorMessage(error);
      alert.error(errorMessage);
      onSubmit?.({ id: sectionId, ...section });
    },
  });

  const handleDelete = async () => {
    if (isUnique) {
      alert.warning(t('notDeleteUniqueSection'));
      return Promise.reject();
    }

    if (!sectionId) {
      onDeleteSection?.({ ...section, id: sectionId });
      return;
    }

    return deleteSection({
      ...chapterParams,
      sectionId,
    });
  };

  const { mutate: onDelete, isPending: isLoadingDelete } = useMutation(
    handleDelete,
    {
      onSuccess: () => onDeleteSection?.({ ...section, id: sectionId }),
      onError: error => {
        if (isUnique) return;
        const errorMessage = getErrorMessage(error);
        alert.error(errorMessage);
      },
    },
  );

  return (
    <SectionForm
      isLoading={isLoadingEdit}
      isDeleting={isLoadingDelete}
      hasChanges={hasChanges}
      onSubmit={debounce(onEdit, 500)}
      onDelete={onDelete}
      section={cloneDeep({ id: sectionId, ...section })}
      {...chapterParams}
    />
  );
}
